// Conversation du chatbot : historique, envoi, polling de la réponse,
// cartes d'action et quota. Réutilisable par la page /chatbot.
//
// L'état vit dans le widget persistant : il survit aux navigations Inertia.
// Tous les minuteurs sont arrêtés au démontage.
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { router } from '@inertiajs/react'
import { appFetch } from '@/lib/http'
import {
  formatDate, formatMessageContent, isWebviewMode, normalizeSystemAction, parseSystemActionFromContent,
  preserveWebviewInUrl, texts, warn,
} from '@/components/chatbot/format'
import type { ChatItem, HistoryRow, SystemAction } from '@/components/chatbot/types'

// Cadence de l'interrogation de /chatbot/message_status, partagée avec la
// page /chatbot (components/chatbot/page/useChatbotPage.ts).
export const POLL_TIMEOUT = 5 * 60 * 1000
const HUMOR_THRESHOLD = 20 * 1000
const LOADING_MESSAGE_INTERVAL = 5000
export const POLL_DELAY = 1000
export const MAX_POLL_ERRORS = 20
const TEXTAREA_MAX_HEIGHT = 150

type Message = HistoryRow & { isError?: boolean; retryContent?: string | null; messageId?: number | string | null }

/**
 * Hauteur du champ selon son contenu. Vide : la hauteur revient à la feuille
 * de style (WebKit compte le placeholder dans scrollHeight).
 */
export function autoResizeTextarea(textarea: HTMLTextAreaElement | null): void {
  if (!textarea) return
  if (!textarea.value) {
    textarea.style.height = ''
    textarea.style.overflowY = 'hidden'
    return
  }
  textarea.style.height = 'auto'
  // scrollHeight exclut la bordure, la hauteur inline (border-box) l'inclut.
  const borderDelta = textarea.offsetHeight - textarea.clientHeight
  const newHeight = Math.min(textarea.scrollHeight + borderDelta, TEXTAREA_MAX_HEIGHT)
  textarea.style.height = `${newHeight}px`
  textarea.style.overflowY = newHeight >= TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden'
}

// Message d'erreur d'une réponse JSON (`error` peut être un simple booléen).
function errorMessageOf(data: Record<string, unknown>): string {
  if (typeof data.message === 'string' && data.message) return data.message
  if (typeof data.error === 'string' && data.error) return data.error
  return texts.error()
}

type Options = {
  hasSubscription: boolean
  subscriptionUrl: string
  remainingRequests: number
  inputRef: RefObject<HTMLTextAreaElement | null>
  /** Ferme le panneau (avant une redirection). */
  onClose: () => void
}

export function useChatbotConversation({ hasSubscription, subscriptionUrl, remainingRequests, inputRef, onClose }: Options) {
  const [items, setItems] = useState<ChatItem[]>([])
  const [showWelcome, setShowWelcome] = useState(true)
  const [loadingText, setLoadingText] = useState<string | null>(null)
  const [remaining, setRemaining] = useState(remainingRequests)
  // Compteur réécrit côté client (updateRemainingRequests) depuis le rendu serveur
  const [remainingFromClient, setRemainingFromClient] = useState(false)
  const [sendDisabled, setSendDisabledState] = useState(false)

  const sendDisabledRef = useRef(false)
  const lastSentRef = useRef<string | null>(null)
  // Question en cours de traitement (id renvoyé par send_message)
  const pollingIdRef = useRef<string | null>(null)
  const keyRef = useRef(0)
  const mountedRef = useRef(true)
  const timeoutsRef = useRef(new Set<number>())
  const intervalsRef = useRef(new Set<number>())
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Nouvelle visite : le compteur du serveur fait foi.
  useEffect(() => {
    setRemaining(remainingRequests)
    setRemainingFromClient(false)
  }, [remainingRequests])

  useEffect(() => {
    mountedRef.current = true
    const timeouts = timeoutsRef.current
    const intervals = intervalsRef.current
    return () => {
      mountedRef.current = false
      timeouts.forEach((id) => window.clearTimeout(id))
      intervals.forEach((id) => window.clearInterval(id))
      timeouts.clear()
      intervals.clear()
    }
  }, [])

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timeoutsRef.current.delete(id)
      if (mountedRef.current) fn()
    }, ms)
    timeoutsRef.current.add(id)
  }, [])

  const every = useCallback((fn: () => void, ms: number) => {
    const id = window.setInterval(fn, ms)
    intervalsRef.current.add(id)
    return () => {
      window.clearInterval(id)
      intervalsRef.current.delete(id)
    }
  }, [])

  const setSendDisabled = useCallback((value: boolean) => {
    sendDisabledRef.current = value
    setSendDisabledState(value)
  }, [])

  // ─── Construction des éléments ────────────────────────────────────────────

  const textItem = (role: string, content: string | null, isError = false, createdAt: string | null = null, retryContent: string | null = null): ChatItem => {
    const toRetry = retryContent || lastSentRef.current
    return {
      kind: 'text',
      key: ++keyRef.current,
      role,
      html: formatMessageContent(content),
      error: isError,
      time: createdAt ? formatDate(createdAt) : texts.justNow(),
      retryContent: isError && toRetry ? toRetry : null,
    }
  }

  const actionItem = (action: SystemAction, createdAt: string | null = null, messageId: number | string | null = null, performed = false): ChatItem => {
    if (!performed && !messageId) console.warn('Button created without messageId - action execution will fail')
    return {
      kind: 'action',
      key: ++keyRef.current,
      messageId: messageId ? String(messageId) : null,
      action,
      time: createdAt ? formatDate(createdAt) : texts.justNow(),
      state: performed ? 'started' : 'idle',
      resultText: performed ? texts.actionStarted() : null,
      upgradeUrl: null,
    }
  }

  const build = (msg: Message): ChatItem[] => {
    if (msg.status === 'failed' && msg.role === 'user') {
      lastSentRef.current = msg.content
      return [
        ...build({ role: 'user', content: msg.content, created_at: msg.created_at }),
        ...build({ role: 'assistant', content: texts.error(), isError: true, retryContent: msg.content }),
      ]
    }
    const systemAction = msg.role === 'assistant' && msg.system_action ? normalizeSystemAction(msg.system_action) : null
    if (systemAction) {
      const out: ChatItem[] = []
      // Texte d'accompagnement d'abord, dans sa propre bulle (jamais le JSON
      // brut d'une ancienne action)
      if (msg.content && msg.content.trim() && !parseSystemActionFromContent(msg.content)) {
        out.push(textItem('assistant', msg.content, false, msg.created_at || null))
      }
      out.push(actionItem(systemAction, msg.created_at || null, msg.id || msg.messageId || null, msg.action_performed || false))
      return out
    }
    if (msg.role === 'assistant' && !msg.isError) {
      const legacy = parseSystemActionFromContent(msg.content)
      if (legacy) return [actionItem(legacy, msg.created_at || null, msg.id || null, false)]
    }
    return [textItem(msg.role, msg.content, msg.isError || false, msg.created_at || null, msg.retryContent || null)]
  }

  const appendMessage = (msg: Message) => {
    const built = build(msg)
    setShowWelcome(false)
    setItems((prev) => [...prev, ...built])
  }

  const addMessage = (role: string, content: string, isError = false) => appendMessage({ role, content, isError })

  const patchCard = (key: number, patch: Partial<Extract<ChatItem, { kind: 'action' }>>) => {
    setItems((prev) => prev.map((it) => (it.kind === 'action' && it.key === key ? { ...it, ...patch } : it)))
  }

  // ─── Formulaire, quota, indicateur ────────────────────────────────────────

  const updateRemaining = (count: unknown) => {
    if (typeof count !== 'number') return
    setRemaining(count)
    setRemainingFromClient(true)
    // updateRemainingRequests écrivait dans tous les #remaining-requests du
    // document, compteur de la page /chatbot compris : il suit l'événement.
    window.dispatchEvent(new CustomEvent('northvane:chatbot:remaining', { detail: count }))
  }

  const enableForm = () => {
    const input = inputRef.current
    if (input) {
      // Pas de focus dans l'app : le clavier surgirait.
      if (!isWebviewMode()) input.focus()
      autoResizeTextarea(input)
    }
    setSendDisabled(false)
  }

  // Seul l'ENVOI est bloqué : le champ n'est jamais désactivé (iOS avale
  // alors chaque tap sans rien montrer).
  const disableForm = () => setSendDisabled(true)

  const showLoadingIndicator = () => {
    setShowWelcome(false)
    setLoadingText(texts.loadingMessages()[0] || texts.typing())
  }
  const hideLoadingIndicator = () => setLoadingText(null)

  // ─── Historique ───────────────────────────────────────────────────────────

  const renderHistory = (history: HistoryRow[] | null | undefined) => {
    if (!history || history.length === 0) {
      setItems([])
      setShowWelcome(true)
      return
    }
    // Lignes en attente ignorées, sauf la question dont on attend la réponse :
    // elle restait masquée à la réouverture du panneau.
    const built = history
      .filter((m) => !((m.status === 'pending' || m.status === 'processing') && !(m.role === 'user' && m.id != null && String(m.id) === pollingIdRef.current)))
      .flatMap((m) => build(m))
    setShowWelcome(false)
    setItems(built)
  }

  const loadHistory = async () => {
    if (!hasSubscription) return
    try {
      const response = await appFetch('/chatbot/history')
      if (!response.ok) return
      const data = await response.json()
      if (!mountedRef.current) return
      renderHistory(data.history)
      updateRemaining(data.remaining_requests)
      if (data.remaining_requests === 0) disableForm()
    } catch {
      // Silencieux : l'historique reste vide
    }
  }

  const fetchMessageById = async (messageId: number | string) => {
    try {
      const response = await appFetch(`/chatbot/messages/${messageId}`)
      if (response.ok) return (await response.json()) as HistoryRow
    } catch (error) {
      console.error('Error fetching message:', error)
    }
    return null
  }

  // Ancien format : retrouve la dernière réponse porteuse d'une action.
  const findAssistantMessageFromHistory = async () => {
    try {
      const response = await appFetch('/chatbot/history')
      if (response.ok) {
        const data = await response.json()
        const candidates = (data.history as HistoryRow[]).filter(
          (m) => m.role === 'assistant' && (m.system_action || (m.content && parseSystemActionFromContent(m.content))),
        )
        if (candidates.length > 0) {
          const last = candidates[candidates.length - 1]
          if (last.id) return await fetchMessageById(last.id)
          return last
        }
      }
    } catch (error) {
      console.error('Error finding assistant message:', error)
    }
    return null
  }

  // ─── Envoi et polling ─────────────────────────────────────────────────────

  const startPolling = (messageId: number | string) => {
    const startTime = Date.now()
    let pollErrors = 0
    pollingIdRef.current = String(messageId)

    const stopTicker = every(() => {
      const humor = Date.now() - startTime >= HUMOR_THRESHOLD
      const messages = humor ? texts.humorMessages() : texts.loadingMessages()
      const message = messages[Math.floor(Math.random() * messages.length)]
      setLoadingText((current) => (current === null ? null : message))
    }, LOADING_MESSAGE_INTERVAL)
    const stopRotation = () => {
      stopTicker()
      if (pollingIdRef.current === String(messageId)) pollingIdRef.current = null
    }

    const poll = async () => {
      if (!mountedRef.current) return
      if (Date.now() - startTime > POLL_TIMEOUT) {
        stopRotation()
        hideLoadingIndicator()
        addMessage('assistant', texts.timeout(), true)
        enableForm()
        return
      }

      try {
        const response = await appFetch(`/chatbot/message_status/${messageId}`)
        const data = await response.json()
        if (!mountedRef.current) return

        if (data.status === 'completed') {
          stopRotation()
          hideLoadingIndicator()

          let systemAction = data.system_action
          let assistantId = data.assistant_message_id || data.assistantMessageId
          // Ancien format : action en JSON dans le contenu, sans identifiant
          if (!systemAction && !assistantId && data.message) {
            try {
              const parsed = JSON.parse(data.message)
              if (parsed.action_system) {
                const assistantMessage = await findAssistantMessageFromHistory()
                if (assistantMessage && assistantMessage.id) {
                  assistantId = assistantMessage.id
                  systemAction = assistantMessage.system_action
                }
              }
            } catch {
              // texte ordinaire
            }
          }

          appendMessage({
            role: 'assistant',
            content: data.message || '',
            created_at: data.created_at,
            system_action: systemAction,
            id: assistantId,
            action_performed: data.action_performed || false,
          })
          updateRemaining(data.remaining_requests)
          if (data.remaining_requests === 0) disableForm()
          else enableForm()
        } else if (data.status === 'failed') {
          stopRotation()
          hideLoadingIndicator()
          appendMessage({ role: 'assistant', content: warn(data.error || texts.error()), isError: true })
          updateRemaining(data.remaining_requests)
          if (data.remaining_requests === 0) disableForm()
          else enableForm()
        } else {
          later(poll, POLL_DELAY)
        }
      } catch {
        // Réseau coupé : on abandonne après MAX_POLL_ERRORS échecs au lieu de
        // laisser le formulaire bloqué.
        pollErrors += 1
        if (pollErrors > MAX_POLL_ERRORS) {
          stopRotation()
          hideLoadingIndicator()
          addMessage('assistant', warn(texts.networkError()), true)
          enableForm()
          return
        }
        later(poll, POLL_DELAY)
      }
    }

    void poll()
  }

  const sendMessage = async () => {
    if (!hasSubscription) {
      window.location.href = subscriptionUrl
      return
    }
    const input = inputRef.current
    if (!input) return
    const message = input.value.trim()
    if (!message) return
    // Entrée et « réessayer » respectent aussi le bouton désactivé
    if (sendDisabledRef.current) return

    lastSentRef.current = message
    setSendDisabled(true)
    addMessage('user', message)
    input.value = ''
    autoResizeTextarea(input)

    try {
      const response = await appFetch('/chatbot/send_message', { method: 'POST', body: { message } })
      const data = await response.json()
      if (!mountedRef.current) return

      if (data.success && data.message_id) {
        if (data.system_action) {
          hideLoadingIndicator()
          const systemAction = normalizeSystemAction(data.system_action)
          if (systemAction) {
            const card = actionItem(systemAction, null, data.assistant_message_id || data.assistantMessageId || null, false)
            setShowWelcome(false)
            setItems((prev) => [...prev, card])
          }
          updateRemaining(data.remaining_requests)
          enableForm()
        } else {
          showLoadingIndicator()
          startPolling(data.message_id)
        }
        return
      }

      if (data.subscription_required && data.redirect_url) {
        window.location.href = data.redirect_url
        return
      }
      if (data.api_credits_exhausted) {
        hideLoadingIndicator()
        addMessage('assistant', data.message || texts.error(), true)
        disableForm()
        console.error('API Credits Exhausted:', data.details)
        return
      }
      hideLoadingIndicator()
      addMessage('assistant', warn(data.message || texts.error()), true)
      if (data.quota_exceeded) disableForm()
      else enableForm()
    } catch {
      if (!mountedRef.current) return
      hideLoadingIndicator()
      addMessage('assistant', warn(texts.networkError()), true)
      enableForm()
    }
  }

  const retryMessage = (content: string | null) => {
    const message = content || lastSentRef.current
    if (!message || sendDisabledRef.current) return
    // Retire les bulles d'erreur et la dernière question (renvoyée ci-dessous)
    setItems((prev) => {
      const kept = prev.filter((it) => !((it.kind === 'text' && it.error) || (it.kind === 'action' && it.state === 'error')))
      let lastUser = -1
      kept.forEach((it, i) => { if (it.kind === 'text' && it.role === 'user') lastUser = i })
      return lastUser >= 0 ? kept.filter((_, i) => i !== lastUser) : kept
    })
    if (inputRef.current) inputRef.current.value = message
    void sendMessage()
  }

  // ─── Cartes d'action ──────────────────────────────────────────────────────

  // Page de suivi de l'action lancée, 2 s après la fermeture du panneau : une
  // visite Inertia. La visite guidée garde son chargement complet (le tour lit
  // son état au montage de la page).
  const visitLater = (url: string) => later(() => router.visit(preserveWebviewInUrl(url)), 2000)

  const markActionAsExecuted = async (messageId: string) => {
    const numericId = parseInt(messageId, 10)
    if (Number.isNaN(numericId)) throw new Error(`Invalid message ID: ${messageId}`)
    const response = await appFetch(`/chatbot/messages/${numericId}/mark_action_executed`, { method: 'PATCH' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      console.error('Failed to mark action as executed:', response.status, data)
      throw new Error(data.message || 'Failed to mark action as executed')
    }
    return response.json()
  }

  const executeSystemAction = async (cardKey: number, action: SystemAction, messageId: string | null) => {
    if (!messageId) {
      console.error('Button has no messageId!')
      return
    }
    try {
      // D'abord marquer l'action comme faite en base, avant toute autre chose
      await markActionAsExecuted(messageId)
      if (!mountedRef.current) return
      patchCard(cardKey, { state: 'loading' })

      if (action.type === 'launch_tutorial') {
        // Sans phase : simple navigation, pas de visite guidée
        if (action.phase) {
          const tourState = {
            active: true,
            phase: action.phase,
            stepIndex: action.step_index ?? 0,
            stepId: action.step_id || null,
            nextPage: null,
            fromChatbot: true,
          }
          sessionStorage.setItem('onboarding_tour_state', JSON.stringify(tourState))
          sessionStorage.removeItem('onboarding_dismissed')
        }
        onCloseRef.current()
        later(() => { window.location.href = preserveWebviewInUrl(action.page || '/dashboard') }, 400)
        return
      }

      if (action.method === 'POST') {
        const body = action.params ? { params: action.params } : {}
        const response = await appFetch(action.url || '/', { method: 'POST', body })
        const data = await response.json()
        if (!mountedRef.current) return

        if (!response.ok || !data.success || data.error) {
          patchCard(cardKey, {
            state: 'error',
            resultText: errorMessageOf(data),
            upgradeUrl: data.subscription_required && data.redirect_url ? data.redirect_url : null,
          })
          return
        }

        onCloseRef.current()
        if (action.type === 'refresh_portfolio') {
          visitLater('/dashboard')
        } else if (action.type === 'refresh_opportunities') {
          visitLater('/opportunities')
        } else {
          later(() => patchCard(cardKey, { state: 'started', resultText: data.message || texts.actionStarted() }), 2000)
        }
        return
      }

      if (action.type === 'create_simulation' && action.params) {
        const response = await appFetch('/simulations', { method: 'POST', body: { simulation: action.params } })
        const data = await response.json()
        if (!mountedRef.current) return

        if (response.ok && data.success) {
          onCloseRef.current()
          if (data.redirect_url) visitLater(data.redirect_url)
        } else {
          patchCard(cardKey, { state: 'idle' })
          let errorMessage = errorMessageOf(data)
          if (data.subscription_required && data.redirect_url) errorMessage += ` ${texts.upgradeRequired()}`
          addMessage('assistant', warn(errorMessage), true)
        }
        return
      }

      window.location.href = action.url || '/'
    } catch (error) {
      console.error('Error executing system action:', error)
      if (!mountedRef.current) return
      setItems((prev) => prev.map((it) => (it.kind === 'action' && it.key === cardKey && it.state === 'loading' ? { ...it, state: 'idle' } : it)))
      addMessage('assistant', warn(texts.error()), true)
    }
  }

  return {
    items,
    showWelcome,
    loadingText,
    remaining,
    remainingFromClient,
    sendDisabled,
    loadHistory,
    sendMessage,
    retryMessage,
    executeSystemAction,
  }
}
